import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Tabs, Select } from 'antd';
import { browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import VendorTraceChart from './VendorTraceChart';
import {
    setConsentTypeFilter,
    setPriorToConsentFilterList,
    setSearchByVendorNameMode,
    setSelectedVendorTracePrevalenceFilters,
} from '../../../actions/diagnose_vendor_trace_actions';
import useSearchParams from '../../diagnose_dashboard/shared/useSearchParams';

const { TabPane } = Tabs;
const Option = Select.Option;

const filterOption = (input, option) =>
option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
|| option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0;
const filterSort = (optionA, optionB) =>
optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase());


const generateTreeData = (vendors) => {
    return [...vendors]
        /* Sort from last level to first level */
        .sort((vendorLevelA, vendorLevelB) => vendorLevelB[0].trace_level - vendorLevelA[0].trace_level)
        .reduce((tree, level) => {
        return level.reduce((normalized, vendor) => {
            const parentKey = vendor.parental_map_key;
            const vendorNode = {
                name: vendor.vendor_name,
                attributes: { ...vendor, hasChildren: !!tree[vendor.map_key]?.length },
                children: tree[vendor.map_key] || [],
            };

            if (!parentKey) {
                return vendorNode;
            }

            // eslint-disable-next-line no-param-reassign
            normalized[parentKey] = normalized[parentKey] || [];
            normalized[parentKey].push(vendorNode);

            return normalized;
        }, {});
    }, {});
};

const VendorTraceOverview = ({
    vendorTraces,
    maxTraceLevel,
    currentTraceLevel,
    consentTypes,
    defaultConsentType,
    selectedVendorTraceFilters,
    selectedVendorTracePrevalenceFilter,
    isCollapseChildren,
    setConsentTypeFilter,
    consentTypeFilter,
    setSelectedVendorTracePrevalenceFilters,
    searchByVendorNameMode, setSearchByVendorNameMode,
    priorToConsentFilterList,
    setPriorToConsentFilterList,
}) => {
    const [chartData, setChartData] = useState({});
    const [filteredChartData, setFilteredChartData] = useState({});
    const [selectedMetric, setSelectedMetric] = useState(null);
    const [selectedVendorName, setSelectedVendorName] = useState(null);
    const vendorTracesFlat = useMemo(() => vendorTraces.flat(), [vendorTraces]);
    const vendorNames = useMemo(() => {
        return [...new Set(vendorTracesFlat.map(({ vendor_name: name }) => name))];
    }, [vendorTraces]);
    const searchParams = useSearchParams();
    const vendorCalls = searchParams?.vendor_calls?.split(',') || [];
    const highlightChain = useMemo(() => {
        if (!(searchParams.key || searchParams.vendor)) return [];
        const initialCurrentVendor = vendorTracesFlat.find((vendor) => (
            vendor.map_key === searchParams.key
            && vendor.parental_map_key === searchParams.parental
        ));

        if (!initialCurrentVendor) return [];

        const buildChain = (vendor, chain = []) => {
            const newChain = [vendor.map_key, ...chain];
            const parentVendor = vendorTracesFlat.find((vendorTrace) => vendorTrace.map_key === vendor.parental_map_key);
            return parentVendor ? buildChain(parentVendor, newChain) : newChain;
        };

        return [...buildChain(initialCurrentVendor), ...vendorCalls];
    }, [vendorTracesFlat, searchParams]);

    useEffect(() => {
        setSelectedMetric(null);
        setSelectedVendorName(null);
        setConsentTypeFilter(null);
    }, [selectedVendorTraceFilters]);

    useEffect(() => {
        setFilteredChartData({});
        setChartData({});
        setPriorToConsentFilterList([true, false]);
    }, [consentTypeFilter]);

    useEffect(() => {
        if (!vendorTracesFlat.length) {
            setSelectedMetric(null);
            setSelectedVendorName(null);
            setSelectedVendorTracePrevalenceFilters([0, 100]);
        }
    }, [vendorTracesFlat]);

    const collapseDataToSecondLayer = (children) => {
        const filteredChildren = [];
        children.forEach((child) => {
            const clone = _.cloneDeep(child);
            if (child.children.length) {
                clone.children = [];
            }
            filteredChildren.push(clone);
        });
        return filteredChildren;
    };

    const isMatchPrevalence = (node, prevalence_filter) => {
            const prevalence = Math.round(node.attributes.relative_prevalence * 100);
            if ((prevalence >= prevalence_filter[0] && prevalence <= prevalence_filter[1])) {
                return true;
            }
        return false;
    };

    const isChildrenHaveMatchPrevalence = (node, prevalence_filter) => {
            for (let i = 0; i < node.children.length; i += 1) {
                const prevalence = Math.round(node.children[i].attributes.relative_prevalence * 100);
                if ((prevalence >= prevalence_filter[0] && prevalence <= prevalence_filter[1])) {
                    return true;
                } 
                    isChildrenHaveMatchPrevalence(node.children[i], prevalence_filter);
            }
        return false;
    };

    const filterDataByPrevalence = (children) => {
        const filteredChildren = [];
        children.forEach((child) => {
            if (isMatchPrevalence(child, selectedVendorTracePrevalenceFilter) || isChildrenHaveMatchPrevalence(child, selectedVendorTracePrevalenceFilter)) {
                const clone = {};
                for (const key in child) {
                    clone[key] = child[key];
                }
                clone.children = filterDataByPrevalence(child.children);
                filteredChildren.push(clone);
            }
        });
        return filteredChildren;
    };


    useEffect(() => {
        if (maxTraceLevel === currentTraceLevel) {
            const generatedChartData = generateTreeData(vendorTraces);
            setChartData(generatedChartData);
            setFilteredChartData(generatedChartData);
        }
    }, [vendorTraces]);

    useEffect(() => {
        if (!_.isEmpty(chartData)) {
            setFilteredChartData(isCollapseChildren ? {
                name: chartData.name,
                attributes: chartData.attributes,
                children: collapseDataToSecondLayer(chartData.children),
            } : chartData);
        }
    }, [isCollapseChildren]);

    useEffect(() => {
        if (!_.isEmpty(chartData)) {
            if (searchByVendorNameMode === 'searchedVendors') {
                setFilteredChartData(chartData);
            } else {
                onSelectVendorName(selectedVendorName);
            }
        }
    }, [searchByVendorNameMode]);

    useEffect(() => {
        if (!_.isEmpty(chartData)) {
            browserHistory.replace(location.pathname);
            onSelectPriorToConsentFilter(priorToConsentFilterList);
        }
    }, [priorToConsentFilterList]);

    useEffect(() => {
        if (!_.isEmpty(chartData)) {
            const chartDataByPrevalence = _.cloneDeep(chartData);
            if (selectedVendorName) {
                chartDataByPrevalence.children = filterDataByVendorName(chartData.children, selectedVendorName);
            }
            if (priorToConsentFilterList.length === 1) {
                chartDataByPrevalence.children = filterDataByPriorToConsent(chartData.children, priorToConsentFilterList[0]);
            }
            browserHistory.replace(location.pathname);
            setFilteredChartData({
                name: chartDataByPrevalence.name,
                attributes: chartDataByPrevalence.attributes,
                children: filterDataByPrevalence(chartDataByPrevalence.children),
            });
        }
    }, [selectedVendorTracePrevalenceFilter]);


    const getChildrenByMapKey = (data, id) => {
        if (data.attributes.map_key === id) {
            return collapseDataToSecondLayer(data.children);
        }

        for (const child of data.children) {
            const children = getChildrenByMapKey(child, id);
            if (children.length) {
                return children;
            }
        }

        return [];
    };

    const filterDataByMapKey = (data, id, filteredData) => {
        const chartDataWithToggledChildren = _.cloneDeep(filteredData);
        if (chartDataWithToggledChildren.attributes.map_key === id) {
            if (!chartDataWithToggledChildren.attributes.hasChildren) {
                return chartDataWithToggledChildren;
            }

            if (chartDataWithToggledChildren.children.length) {
                chartDataWithToggledChildren.children = [];
            } else {
                chartDataWithToggledChildren.children = getChildrenByMapKey(data, id);
            }
            return chartDataWithToggledChildren;
        }
        const filteredChildren = [];
        chartDataWithToggledChildren.children.forEach((child) => {
            filteredChildren.push(filterDataByMapKey(data, id, child));
        });
        chartDataWithToggledChildren.children = filteredChildren;

        return chartDataWithToggledChildren;
    };
    const onToggleChildren = (id) => {
        setFilteredChartData(filterDataByMapKey(chartData, id, filteredChartData));
    };
    const onChange = (key) => {
        browserHistory.replace(location.pathname);
        setConsentTypeFilter(key);
    };

    const onSelectMetric = (metric) => {
        browserHistory.replace(location.pathname);
        setSelectedMetric(metric);
    };
    const isAnyChildrenMatchVendorName = (name, children) => {
        for (const child of children) {
            if (child.name === name || isAnyChildrenMatchVendorName(name, child.children)) {
                return true;
            }
        }
        return false;
    };

    const filterDataByVendorName = (children, vendorName) => {
        const filteredChildren = [];
        children.forEach((child) => {
            if (child.name === vendorName) {
                const clone = _.cloneDeep(child);
                filteredChildren.push(clone);
            } else if (isAnyChildrenMatchVendorName(vendorName, child.children)) {
                const clone = _.cloneDeep(child);
                clone.children = filterDataByVendorName(child.children, vendorName);
                filteredChildren.push(clone);
            }
        });
        return filteredChildren;
    };
    const onSelectVendorName = (vendorName) => {
        setSearchByVendorNameMode('allVendors');
        setSelectedVendorName(vendorName);
        const chartDataByVendorName = _.cloneDeep(chartData);
        chartDataByVendorName.children = filterDataByPrevalence(chartData.children);
        if (vendorName) {
            setSelectedVendorTracePrevalenceFilters([0, 100]);
            chartDataByVendorName.children = filterDataByVendorName(chartData.children, vendorName);
        }
        setFilteredChartData(chartDataByVendorName);
        browserHistory.replace(location.pathname);
    };

    const isAnyChildrenMatchPriorToConsent = (priorToConsentFilter, children) => {
        for (const child of children) {
            if (child.attributes.is_prior_to_consent === priorToConsentFilter || isAnyChildrenMatchPriorToConsent(name, child.children)) {
                return true;
            }
        }
        return false;
    };

    const filterDataByPriorToConsent = (children, priorToConsentFilter) => {
        const filteredChildren = [];
        if (priorToConsentFilter) {
            children.forEach((child) => {
                if (child.attributes.is_prior_to_consent === priorToConsentFilter) {
                    const clone = _.cloneDeep(child);
                    if (child.children.length) {
                        clone.children = filterDataByPriorToConsent(child.children, priorToConsentFilter);
                    }
                    filteredChildren.push(clone);
                }
            });
        } else {
            children.forEach((child) => {
                if (child.attributes.is_prior_to_consent === priorToConsentFilter) {
                    const clone = _.cloneDeep(child);
                    filteredChildren.push(clone);
                } else if (isAnyChildrenMatchPriorToConsent(priorToConsentFilter, child.children)) {
                    const clone = _.cloneDeep(child);
                    clone.children = filterDataByPriorToConsent(child.children, priorToConsentFilter);
                    filteredChildren.push(clone);
                }
            });
        }

        return filteredChildren;
    };

    const onSelectPriorToConsentFilter = (priorToConsentFilter) => {
        const chartDataByPriorToConsent = _.cloneDeep(chartData);
        chartDataByPriorToConsent.children = filterDataByPrevalence(chartData.children);
        if (priorToConsentFilter.length === 1) {
            setSelectedVendorTracePrevalenceFilters([0, 100]);
            chartDataByPriorToConsent.children = filterDataByPriorToConsent(chartData.children, priorToConsentFilter[0]);
        }
        setFilteredChartData(chartDataByPriorToConsent);
    };


    const operations = (<div>
        <Select
            getPopupContainer={(trigger) => trigger.parentElement}
            showSearch
            style={{ marginRight: 10 }}
            allowClear
            disabled={!maxTraceLevel}
            size="medium"
            placeholder="Search Vendor"
            optionFilterProp="children"
            filterOption={filterOption}
            filterSort={filterSort}
            onChange={onSelectVendorName}
        >
            {vendorNames?.map((name) => <Option key={name} value={name}>{name}</Option>)}
        </Select>
        <Select
            getPopupContainer={(trigger) => trigger.parentElement}
            showSearch
            allowClear
            size="medium"
            disabled={!maxTraceLevel}
            placeholder="Select a filter"
            optionFilterProp="children"
            filterOption={filterOption}
            filterSort={filterSort}
            onChange={onSelectMetric}
        >
            <Option
                title="This setting overlays colour coding onto the vendor trace that displays for whether or not the vendors we have picked up are disclosed on your vendor list."
                value="is_disclosed"
            >Non-Disclosed Vendors</Option>
            {/* <Option value="is_data_leaving_eea">Data leaving the EEA</Option> */}
            <Option
                title="This setting displays all the vendors that are possibly fingerprinting on the vendor trace."
                value="is_fingerprinting"
            >Possible Fingerprinting</Option>
            <Option
                title="This setting displays all the vendors that are storing identifiers via cookies or local storage on the vendor trace."
                value="is_local_storage"
            >Information, Storage & Access</Option>
            {/* <Option */}
            {/*    title="This setting displays all the vendors that were triggered in scans prior to the user accepting all settings on the consent banner." */}
            {/*    value="is_prior_to_consent" */}
            {/* >Prior to consent</Option> */}
            {/* <Option value="is_cookie_present">All cookies found</Option> */}
        </Select>
    </div>);

    useEffect(() => {
        if (!highlightChain.length || !Object.keys(chartData).length) {
            return;
        }

        const filterNode = (node) => {
            const { hasChildren, ...nodeAttributes } = node?.attributes || {};
            const isVendorCalled = vendorCalls.includes(nodeAttributes.map_key);
            const isInChain = highlightChain.includes(nodeAttributes.map_key);
            return {
                ...node,
                attributes: {
                    ...node.attributes,
                    highlighted: isInChain,
                },
                children: !isInChain || isVendorCalled ? [] : node.children.map(filterNode).sort((a, b) => +b.attributes.highlighted - +a.attributes.highlighted),
            };
        };

        const highlightChart = filterNode(chartData);
        setFilteredChartData(highlightChart);
    }, [vendorTraces, chartData, highlightChain]);


    return (
        <div className={'vendor_trace_container'} id={'wrapper'}>
            {maxTraceLevel === currentTraceLevel && consentTypes ?
                <Tabs defaultActiveKey={defaultConsentType} onChange={onChange} tabBarExtraContent={operations}>
                    {consentTypes.map((consentType) => {
                        return (
                            <TabPane tab={consentType.name} key={consentType.id}>
                                {maxTraceLevel ? (
                                    <VendorTraceChart
                                        consentTypeName={consentType.name}
                                        consentTypeDescription={consentType.definition}
                                        filteredData={filteredChartData}
                                        selectedMetric={selectedMetric}
                                        selectedVendorName={selectedVendorName}
                                        maxTraceLevel={maxTraceLevel}
                                        toggleChildren={(e) => onToggleChildren(e)}
                                    />
                                ) : (
                                    <Empty description={<span>There is no data for selected consent type</span>} />
                                )}
                            </TabPane>
                        );
                    })}
                </Tabs>
                :
                <Empty description={<span>Select property to view data</span>} />
            }
        </div>
    );
};
const mapStateToProps = function (store) {
    return {
        selectedVendorTraceFilters: store.diagnoseVendorTraceState.getIn(['vendorTraceFilters', 'value']),
        selectedVendorTracePrevalenceFilter: store.diagnoseVendorTraceState.getIn(['vendorTracePrevalenceFilter', 'value']),
        isCollapseChildren: store.diagnoseVendorTraceState.getIn(['isCollapseChildren', 'value']),
        searchByVendorNameMode: store.diagnoseVendorTraceState.getIn(['searchByVendorNameMode', 'value']),
        consentTypeFilter: store.diagnoseVendorTraceState.getIn(['consentTypeFilter', 'value']),
        priorToConsentFilterList: store.diagnoseVendorTraceState.getIn(['priorToConsentFilterList', 'value']),
    };
};
const mapDispatchToProps = (dispatch) => {
    return bindActionCreators({
        setConsentTypeFilter,
        setSelectedVendorTracePrevalenceFilters,
        setSearchByVendorNameMode,
        setPriorToConsentFilterList,
    }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorTraceOverview);
